import { Component, OnInit, TemplateRef, HostListener, Inject, LOCALE_ID, ViewChild } from '@angular/core';
import { ContactService } from '../../services/contact/contact.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Contact, getTagArrayFromString } from 'src/classes/contact';
import { ContactFormModalComponent } from './contact-form-modal/contact-form-modal.component';
import { BulkSendPdfPasswordModalComponent } from './bulk-send-pdf-password-modal/bulk-send-pdf-password-modal.component';
import { limitStringLength } from 'src/utils/limitStringLength';
import { ActivatedRoute } from '@angular/router';

interface ContactOption {
  contact: Contact;
  isSelected: boolean;
  isLoading?: boolean;
}

@Component({
  selector: 'app-contact-list-page',
  templateUrl: './contact-list-page.component.html',
  styleUrls: ['./contact-list-page.component.scss']
})
export class ContactListPageComponent implements OnInit {
  public importResultData = null;
  public modalErrorMesssage = '';
  public modalBulkRemErrorMesssage = '';
  public sendingFile = false;
  public showImportErrors = false;
  public fileToUpload: File = null;
  public isEnabledUpdateBulk = false;
  public templateFileUrl = '/assets/TemplateContactList.xlsx';
  public contactOptionList: ContactOption[];
  public isLoadingTasks = false;
  public viewTasks = false;
  public contactTasks: any;

  public headers = [
    {class: 'name', label: 'Name', filter: {property: 'name', value: '', open: false, width: 0}},
    {class: 'email', label: 'Email', filter: {property: 'email', value: '', open: false, width: 0}},
    {class: 'phone', label: 'Phone', filter: {property: 'phone', value: '', open: false, width: 0}},
    {class: 'tags', label: 'Tags', filter: {property: 'tags', value: '', open: false, width: 0}},
    {class: 'department', label: 'Department', filter: {property: 'department', value: '', open: false, width: 0}},
    {class: 'company', label: 'Company', filter: {property: 'company', value: '', open: false, width: 0}},
    {class: 'password', label: 'PDF Password'},
    {class: 'actions', label: 'Actions'},
  ];

  public filters: {property: string, value: string}[] = [];
  public filteredContactOptionList = [];
  public availableTags = [];

  constructor(
    private contactService: ContactService,
    public modalService: NgbModal,
    @Inject(LOCALE_ID) public locale: string,
    private route: ActivatedRoute,
  ) { }

  ngOnInit() {
    const tag = this.route.snapshot.paramMap.get('tag');
    if (tag) {
      this.headers[3].filter.value = tag;
    }
    this.fetchContactList();
    this.applyLocaleToHeader();
  }

  private applyLocaleToHeader() {
    if (typeof this.locale === "string" && this.locale.toLowerCase().substring(0, 2) === "pt") {
      const portugueseList = ["Nome", "Email", "Telefone", "Tags", "Departamento", "Empresa", "Senha PDF", "Ações"];

      portugueseList.forEach((portugueseLabel, index) => this.headers[index].label = portugueseLabel);
    }
  }

  shouldIncludeContact(option: ContactOption) {
    const contact = option.contact;
    for (const filter of this.filters) {
      if (!filter.property || typeof contact[filter.property] !== 'string') {
        if (filter.property === "tags") {
          if (filter.value !== "") {
            return false;
          }
        } else {
          console.warn(`Could not filter by property ${JSON.stringify(filter.property)} because it doesn't exist at contact`);
        }
        continue;
      }
      if (!contact[filter.property].toLowerCase().includes(filter.value.toLowerCase())) {
        return false;
      }
    }
    return true;
  }

  onFilterChange() {
    this.updateFilteredContacts();
  }

  onTagClick(tag) {
    this.headers[3].filter.value = tag;
    this.onFilterChange();
  }

  updateFilteredContacts() {
    for (let index = 0; index < this.headers.length; index++) {
      if (!this.headers[index] || !this.headers[index].filter) {
        continue;
      }
      const wrapper = document.querySelector(`app-contact-list-page [data-index='${index}']`);
      const input = wrapper ? wrapper.parentNode.querySelector('input') : null;
      if (!input) {
        continue;
      }
      this.headers[index].filter.value = input.value;
    }
    this.filters = this.headers.filter(header => header.filter).map(
      header => ({
        property: header.filter.property,
        value: header.filter ? header.filter.value : null
      })
    );
    this.filteredContactOptionList = this.contactOptionList.filter(this.shouldIncludeContact.bind(this));
  }

  onFilterButtonDisable(index: number, wrapper: HTMLElement, input: HTMLInputElement) {
    const header = this.headers[index];
    header.filter.open = false;
    header.filter.value = input ? input.value : '';
  }

  onFilterButtonEnable(index: number, wrapper: HTMLElement, input: HTMLInputElement) {
    const header = this.headers[index];
    header.filter.width = wrapper ? wrapper.getBoundingClientRect().width - 12 : 100;
    header.filter.open = true;
  }

  onClearFilterClick(header: any) {
    header.filter.value = '';
    this.updateFilteredContacts();
  }

  // Detect FocusOut via global click to unblur filters
  @HostListener('window:click', ['$event'])
  onWindowClick(event) {
    let clickedButton: HTMLButtonElement;
    let clickedId: number;

    for (const node of event.composedPath()) {
      if (!node || !node.classList) {
        continue;
      }
      if (node.classList.contains('filter-input-wrapper')) {
        const wrapper = node.parentNode.parentNode;
        if (!wrapper.hasAttribute('data-index')) {
          console.warn(`Filter input wrapper is missing data-index`);
          continue;
        }
        clickedId = parseInt(wrapper.getAttribute('data-index'), 10);
      } else if (node.classList.contains('btn-filter')) {
        const button = node;
        const wrapper = node.parentNode.parentNode;
        if (!wrapper.hasAttribute('data-index')) {
          console.warn(`Filter button wrapper is missing data-index`);
          continue;
        }
        clickedButton = button;
        clickedId = parseInt(wrapper.getAttribute('data-index'), 10);
      }
    }

    for (let index = 0; index < this.headers.length; index++) {
      if (!this.headers[index] || !this.headers[index].filter) {
        continue;
      }
      const wrapper: HTMLElement = document.querySelector(`app-contact-list-page .filter-table-header[data-index='${index}']`);
      if (!wrapper) {
        console.warn('Could not find wrapper for index', index);
        continue;
      }
      if (clickedId === index) {
        if (clickedButton && this.headers[index].filter.open) {
          this.onFilterButtonDisable(index, wrapper, wrapper.querySelector('input'));
        } else if (clickedButton) {
          this.onFilterButtonEnable(index, wrapper, wrapper.querySelector('input'));
          // Focus the new input
          setTimeout(() => {
            if (wrapper) {
              const subInput = wrapper.querySelector('input');
              if (subInput) {
                subInput.focus();
              }
            }
          }, 100);
        }
      } else {
        if (this.headers[index].filter.open) {
          this.onFilterButtonDisable(index, wrapper, wrapper.querySelector('input'));
        }
      }
    }
  }

  allIntermediate() {
    const checkedCount = this.filteredContactOptionList.filter((i: any) => i.isSelected).length;
    return checkedCount !== 0 && this.filteredContactOptionList.length !== checkedCount;
  }

  handleFileInput(files: FileList, element: any) {
    this.fileToUpload = files.item(0);
    element.value = '';
  }

  async fetchContactList() {
    const response = await this.contactService.listContacts(null, null, null, true);
    const list = response.data;
    let accTags = new Set<string>();
    this.contactOptionList = list.map(c => {
      if (typeof c.idContact !== 'number') {
        console.warn('Incorrect contact id format: ', typeof c.idContact);
        c.idContact = typeof c.idContact === 'string' ? parseInt(c.idContact, 10) : c.idContact;
      }

      if (c.tags) {
        for (let tag of getTagArrayFromString(c.tags)) {
          accTags.add(tag);
        }
      }

      return {
        contact: Object.assign(c, new Contact()),
        isSelected: false,
        isLoading: false
      };
    });
    this.availableTags = Array.from(accTags);
    this.updateFilteredContacts();
  }

  isAllSelected() {
    return this.filteredContactOptionList.length === this.filteredContactOptionList.filter((i: any) => i.isSelected).length;
  }

  onUpdateSwitch(status: boolean) {
    this.isEnabledUpdateBulk = status;
  }

  async onBulkSendPdfPasswordClick() {
    const selection = this.filteredContactOptionList.filter(i => i.isSelected);
    const modal = this.modalService.open(BulkSendPdfPasswordModalComponent);
    modal.componentInstance.contacts = selection.map(i => i.contact);

    let changedContactList = true;
    try {
      changedContactList = await modal.result;
    } catch(err) {}
    if (changedContactList) {
      selection.forEach(option => option.isLoading = true);
      await this.fetchContactList();
    }
  }

  skippedTasks() {
    return this.contactTasks.filter(i=>i.reportContacts==0).length
  }

  async onBulkRemoveClick(modalElement) {
    this.isLoadingTasks = true;
    this.modalService.open(modalElement).result.then((result) => {
      this.contactTasks = null;
      this.viewTasks = false;
    });
    try {
      var contacts = this.filteredContactOptionList.filter((i: any) => i.isSelected).map((i: any) => i.contact.idContact)
      this.contactTasks = await this.contactService.getLinkedTasks(contacts.join(','));
      this.isLoadingTasks = false;
    } catch(e) {
      this.isLoadingTasks = false;
    }
  }

  async onRemoveClick(modalElement, option: ContactOption) {
    const contact = option.contact;
    this.isLoadingTasks = true;

    this.modalService.open(modalElement).result.then((result) => {
      this.contactTasks = null;
      this.viewTasks = false;
      if (result === 'Remove') {
        option.isLoading = true;
        this.contactService.removeContact(contact.idContact).subscribe(async success => {
          this.modalService.dismissAll();
          await this.fetchContactList();
          if (option) {
            option.isLoading = false;
          }
        }, err => {
          alert('Could not remove contact: Try again later or request help');
          window.location.reload();
        });
      }
    });
    try {
      this.contactTasks = await this.contactService.getLinkedTasks(contact.idContact.toString());
      this.isLoadingTasks = false;
    } catch(e) {
      this.isLoadingTasks = false;
    }
  }

  showTasks() {
    this.viewTasks=!this.viewTasks;
  }

  collapseErrors() {
    this.showImportErrors = !this.showImportErrors;
  }

  onSelectContact() {
    if (this.filteredContactOptionList.filter((i: any) => i.isSelected).length === this.filteredContactOptionList.length) {
      this.filteredContactOptionList.map(i => Object.assign(i, { isSelected: false }));
      return;
    }
    this.filteredContactOptionList.map(i => Object.assign(i, { isSelected: true }));
    console.log(this.filteredContactOptionList);
    console.log(this.contactOptionList);
  }

  selectedContacts() {
    return this.filteredContactOptionList.filter((i: any) => i.isSelected).length;
  }

  bulkRemove() {
    this.contactService.removeBulkContact(
      this.filteredContactOptionList.filter((i: any) => i.isSelected).map((i: any) => i.contact.idContact)
    ).subscribe(
      (data) => {
        this.modalService.dismissAll();
        this.fetchContactList();
      },
      (error) => {
        this.modalErrorMesssage = error.error.error.message;
        this.sendingFile = false;
      }
    );
  }

  clearModalBRError() {
    this.modalBulkRemErrorMesssage = '';
  }

  sendFile() {
    this.modalErrorMesssage = '';
    this.sendingFile = true;
    this.contactService.postFile(this.fileToUpload, this.isEnabledUpdateBulk).subscribe((data: any) => {
      this.fileToUpload = null;
      this.sendingFile = false;
      this.importResultData = data;
      if (data.errors.length > 0) {
        this.importResultData.treeData = this.transformErrorData(data.errors);
        console.log(this.importResultData.treeData);
      }
      this.fetchContactList();
    }, error => {
      this.modalErrorMesssage = error.error.error.message;
      this.sendingFile = false;
    });
  }

  transformErrorData(data) {
    const list: {ID: string, NAME: string, PARENT_ID?: string, class?: string}[] = [];
    const fields = ['name', 'title', 'email', 'phone', 'company', 'department', 'pdfpassword'];
    data.forEach((i, idx1) => {
      list.push({ ID: `${idx1}`, NAME: `Error on line ${i.data.index + 1}` });
      fields.forEach(item => {
        if (i.error[item] && i.error[item].length) {
          list.push({
            ID: `${idx1}_${item}`,
            NAME: `Field: ${item}`,
            PARENT_ID: `${idx1}`,
            class: i.error[item] && i.error[item].length ? 'text-danger' : ''
          });
          i.error[item].forEach((element, idx2) => {
            list.push({
              ID: `${idx1}_${item}_${idx2}`,
              NAME: `error: ${element}`,
              PARENT_ID: `${idx1}_${item}`
            });
          });
        }
      });
    });
    return list;
  }

  clearModalBAError(modal: any) {
    this.showImportErrors = false;
    this.importResultData = null;
    if (this.importResultData && (this.importResultData.success > 0 || this.importResultData.updated > 0)) {
      this.fetchContactList();
    }
    modal.dismiss('Close');
  }

  async onAddClick() {
    const newContact = await this.handleContactForm();
    if (newContact) {
      await this.fetchContactList();
    }
  }

  async onEditClick(option: ContactOption) {
    if (await this.handleContactForm(option)) {
      await this.fetchContactList();
    }
  }

  getTagArrayFromString(str: string) {
    return getTagArrayFromString(str);
  }

  async handleContactForm(option?: ContactOption): Promise<Contact> {
    const contact = option ? option.contact : null;
    const isNewContact = !option;
    let initialError = '';
    let result = contact;

    // Loop to keep trying if there's an error while submiting the form
    for (let i = 0; i < 100; i++) {
      result = await this.showContactModal(result, initialError);
      if (!result) {
        break;
      }
      if (isNewContact) {
        this.contactOptionList.push({
          contact: {} as Contact,
          isSelected: false,
          isLoading: true
        });
      }
      try {
        let response;
        // @ts-ignore
        delete result.emailUser;
        // @ts-ignore
        delete result.status;
        if (option) {
          option.isLoading = true;
        }
        if (isNewContact) {
          response = await this.contactService.addContact(result);
        } else {
          response = await this.contactService.editContact(result);
        }
        if (option) {
          option.isLoading = false;
        }
        if (response !== true) {
          const responseString = JSON.stringify(response, null, '\t');
          throw new Error(`Unexpected server response: ${limitStringLength(responseString, 100)}`);
        }
      } catch(err) {
        if (isNewContact) {
          this.contactOptionList.pop(); // Remove 'created' contact
        }
        if (err && err.error && err.error.message && err.error.message.details instanceof Array) {
          initialError = err.error.message.details.map(detail => detail.message).join('\n');
        } else {
          initialError = err ? err.message : err.toString();
        }
        continue;
      }
      return Object.assign(new Contact(), result);
    }
    return null;
  }

  async showContactModal(contact?: Contact, initialError?: string) {
    const modalRef = this.modalService.open(ContactFormModalComponent, { ariaLabelledBy: 'modal-confirm-deletion' });
    if (contact) {
      modalRef.componentInstance.contact = contact;
    }
    modalRef.componentInstance.availableTags = this.availableTags;
    if (initialError) {
      modalRef.componentInstance.initialError = initialError;
    }

    let result: any;
    try {
      result = await modalRef.result;
    } catch(err) {
      result = null;
    }

    return result;
  }

}
